import { Link } from "react-router-dom";
import { Bookmark, Clock, Users } from "lucide-react";
import { useState } from "react";
import ProgressBar from "./ProgressBar";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { resolveMediaUrl } from "@/lib/api";

const formatMoney = (n) => n >= 1000 ? `$${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}K` : `$${n}`;

const CampaignCard = ({ campaign, index = 0 }) => {
  const [saved, setSaved] = useState(false);
  const percent = Math.min(Math.round((campaign.raised / campaign.goal) * 100), 999);
  const daysLeft = campaign.days_left ?? campaign.daysLeft ?? 0;
  const creator = campaign.creator || {};
  const coverUrl = campaign.cover ? resolveMediaUrl(campaign.cover) : "";
  const { requireAuth } = useAuth();

  return (
    <article
      data-testid={`campaign-card-${campaign.id}`}
      style={{ animationDelay: `${index * 80}ms` }}
      className="group relative bg-white rounded-[20px] border border-warm-200 overflow-hidden shadow-warm hover:shadow-warm-lg hover:-translate-y-1 hover:border-warm-300 transition-[transform,box-shadow,border-color] duration-300 animate-fade-up"
    >
      <Link to={`/campaign/${campaign.id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-warm-100 rounded-t-[20px]">
          {coverUrl ? (
            <img
              src={coverUrl} alt={campaign.title} loading="lazy"
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="h-full w-full grid place-items-center text-navy-300 font-display text-2xl">
              {campaign.title?.charAt(0)?.toUpperCase() || "?"}
            </div>
          )}
          <span className="absolute top-3 left-3 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-xs font-semibold text-saffron-700">
            {campaign.category}
          </span>
          <button
            onClick={(e) => {
              e.preventDefault();
              if (!requireAuth("Sign in to save projects to your list.")) return;
              setSaved(!saved);
              toast.success(saved ? "Removed from saved" : "Saved to your list");
            }}
            data-testid={`campaign-card-bookmark-${campaign.id}`}
            className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-white/95 backdrop-blur text-navy-600 hover:text-saffron-600 hover:scale-105 transition-transform"
            aria-label="Bookmark campaign"
          >
            <Bookmark className={`h-4 w-4 ${saved ? "fill-saffron-600 text-saffron-600" : ""}`} />
          </button>
        </div>
      </Link>

      <div className="p-5">
        <div className="flex items-center gap-2 text-xs text-navy-500 mb-2">
          {creator.avatar_url ? <img src={resolveMediaUrl(creator.avatar_url)} alt={creator.name} className="h-5 w-5 rounded-full object-cover" /> : null}
          <span className="font-medium text-navy-600">{creator.name || "Anonymous"}</span>
        </div>

        <Link to={`/campaign/${campaign.id}`}>
          <h3 className="font-display text-lg font-bold text-navy-800 leading-snug tracking-tight group-hover:text-saffron-600 transition-colors line-clamp-2">
            {campaign.title}
          </h3>
        </Link>
        <p className="mt-2 text-sm text-navy-500 line-clamp-2">{campaign.tagline}</p>

        <div className="mt-5">
          <ProgressBar value={percent} />
          <div className="mt-3 flex items-baseline justify-between">
            <div>
              <div className="font-display text-xl font-bold text-navy-800">{formatMoney(campaign.raised)}</div>
              <div className="text-xs text-navy-500">raised of {formatMoney(campaign.goal)}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-xl font-bold text-saffron-600">{percent}%</div>
              <div className="text-xs text-navy-500">funded</div>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-warm-100 flex items-center justify-between text-xs text-navy-500">
          <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {(campaign.backers || 0).toLocaleString()} backers</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {daysLeft} days left</span>
        </div>
      </div>
    </article>
  );
};

export default CampaignCard;
